/*
 * 
 *
 *  THESE ARE TEMPORARY PLACEHOLDERS.
 * The UI/UX team will supply the final Deckorate icon SVGs. Replacing one is a
 * single-line change: swap only the JSX handed to `placeholder(...)` below.
 *
 * Rules for the replacement:
 *   • Do NOT edit the shared wrapper — every icon renders through the same
 *     <svg> so they stay visually consistent (one viewBox, one stroke weight,
 *     `currentColor` so the card tints them).
 *   • Rendered size is controlled by CSS (`.ribIcon svg` in
 *     DeckorateModule.module.css), so a replacement inherits the right
 *     dimensions no matter what width/height its own markup declares.
 *   • Keep the export name — ribbonGroups.tsx wires icons to cards by name.
 */

import type { ComponentType, ReactNode } from "react";

/* Shared wrapper — the single source of icon sizing/stroke/colour. Every
   placeholder below runs through this so swapping inner paths never drifts the
   visual weight. Do not fork this per-icon. */
const placeholder =
  (inner: ReactNode): ComponentType =>
  function DeckIconPlaceholder() {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        data-placeholder-icon="true"
      >
        {inner}
      </svg>
    );
  };

/* ── Replace the JSX below with final UX SVGs (keep the export names) ── */

export const CreateSlidesIcon = placeholder(
  <path d="M10 2l1.5 4.5L16 8l-4.5 1.5L10 14l-1.5-4.5L4 8l4.5-1.5z" />,
);

export const DesignHubIcon = placeholder(
  <>
    <path d="M10 3l7 4-7 4-7-4 7-4z" />
    <path d="M3 11l7 4 7-4" />
  </>,
);

export const ApplyBrandIcon = placeholder(
  <>
    <circle cx="10" cy="10" r="7" />
    <circle cx="10" cy="10" r="4" />
    <circle cx="10" cy="10" r="1" />
  </>,
);

export const BrandCheckIcon = placeholder(
  <>
    <circle cx="10" cy="10" r="7" />
    <path d="M7 10l2 2 4-4" />
  </>,
);

export const FormatToolsIcon = placeholder(
  <>
    <circle cx="10" cy="10" r="2.5" />
    <path d="M10 3v2M10 15v2M3 10h2M15 10h2M5 5l1.4 1.4M13.6 13.6L15 15M5 15l1.4-1.4M13.6 6.4L15 5" />
  </>,
);

/* Ragged-then-flush lines — the mockup's Fix Alignment glyph. */
export const FixAlignIcon = placeholder(<path d="M3 4h14M3 8h9M3 12h12M3 16h7" />);

export const ColorToolsIcon = placeholder(
  <>
    <path d="M10 3a7 7 0 000 14c1 0 1.5-1 1-2s0-2 1-2h1a4 4 0 004-4c0-3.5-3-6-7-6z" />
    <circle cx="7" cy="8" r="1" />
    <circle cx="10" cy="6.5" r="1" />
    <circle cx="13" cy="8" r="1" />
  </>,
);

export const OrgChartIcon = placeholder(
  <>
    <rect x="7.5" y="2.5" width="5" height="4" rx="1" />
    <rect x="2.5" y="13.5" width="5" height="4" rx="1" />
    <rect x="12.5" y="13.5" width="5" height="4" rx="1" />
    <path d="M10 6.5v3M5 13.5v-1a1 1 0 011-1h8a1 1 0 011 1v1" />
  </>,
);

export const MapIcon = placeholder(
  <>
    <path d="M10 2.5c3 0 5 2.2 5 5 0 3.5-5 10-5 10S5 11 5 7.5c0-2.8 2-5 5-5z" />
    <circle cx="10" cy="7.5" r="1.8" />
  </>,
);

export const SpeakerNotesIcon = placeholder(
  <>
    <rect x="3" y="4" width="14" height="9" rx="1.5" />
    <path d="M6 7h8M6 9.5h5" />
    <path d="M8 13l-1.5 3 3-1.5" />
  </>,
);

export const InsightsIcon = placeholder(
  <>
    <path d="M4 15V9M8 15V5M12 15v-7M16 15V3" />
    <path d="M3 17h14" />
  </>,
);

export const CompareIcon = placeholder(<path d="M7 4l-4 6 4 6M13 4l4 6-4 6" />);

/* Circle-in-frame — the Photo Headshot glyph. */
export const PhotoHeadshotIcon = placeholder(
  <>
    <rect x="3" y="3" width="14" height="14" rx="2" />
    <circle cx="10" cy="8.5" r="2.5" />
    <path d="M5 16c1-2.5 3-4 5-4s4 1.5 5 4" />
  </>,
);

/* Dashed sketch box turning into a solid slide box — the Sketch to Slide glyph. */
export const SketchToSlideIcon = placeholder(
  <>
    <rect x="2.5" y="3.5" width="6" height="6" rx="1" strokeDasharray="1.6 1.4" />
    <path d="M9.5 6.5h3M11 5l1.5 1.5L11 8" />
    <rect x="11.5" y="10.5" width="6" height="6" rx="1" />
  </>,
);

/* Ascending pictograph bars — the Convert to Infographic glyph. */
export const InfographicIcon = placeholder(
  <path d="M4 16V11M9 16V7M14 16V4M17 16H3" />,
);

export const ShareIcon = placeholder(
  <>
    <path d="M10 13V4M6 8l4-4 4 4" />
    <path d="M4 16h12" />
  </>,
);

export const CommandIcon = placeholder(
  <>
    <path d="M7 3a2 2 0 110 4H3M13 3a2 2 0 100 4h4M7 17a2 2 0 100-4H3M13 17a2 2 0 110-4h4" />
    <rect x="7" y="7" width="6" height="6" rx="1" />
  </>,
);

/* Four brand marks in a grid — the mockup's Logos glyph. */
export const LogosIcon = placeholder(
  <>
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="11" y="3" width="6" height="6" rx="1" />
    <rect x="3" y="11" width="6" height="6" rx="1" />
    <rect x="11" y="11" width="6" height="6" rx="1" />
  </>,
);
